/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// --- helpers ---
function isVisible(el) {
  if (!el || !el.isConnected) return false;
  // covers display:none, detached, and visibility:hidden
  const cs = getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  // zero box => effectively hidden (common when toggled)
  const hasBox = el.offsetWidth || el.offsetHeight || el.getClientRects().length;
  return !!hasBox && cs.opacity !== '0';
}

function waitForElement(selector, { root = document, timeout = 15000 } = {}) {
  return new Promise((resolve, reject) => {
    const hit = root.querySelector(selector);
    if (hit) return resolve(hit);
    const obs = new MutationObserver(() => {
      const el = root.querySelector(selector);
      if (el) { obs.disconnect(); resolve(el); }
    });
    obs.observe(root === document ? document.documentElement : root, { childList: true, subtree: true });
    if (timeout) setTimeout(() => { obs.disconnect(); reject(new Error('waitForElement timeout')); }, timeout);
  });
}

/**
 * Observe show/hide of an element selected by `selector`.
 * Calls `onChange(visible, el)` and re-watches if the element is removed/re-added.
 */
function observeShowHide(selector, { onChange, root = document } = {}) {
  let currentEl, lastVisible = false;
  let attrObs, domObs, ro, poll;

  const teardown = () => {
    attrObs?.disconnect(); domObs?.disconnect(); ro?.disconnect();
    if (poll) clearInterval(poll);
    attrObs = domObs = ro = poll = null;
    currentEl = null;
  };

  const tick = () => {
    if (!currentEl || !currentEl.isConnected) {
      // element got removed; fire hide once, then restart watcher
      if (lastVisible) { lastVisible = false; onChange?.(false, currentEl); }
      teardown();
      arm(); // wait for it to come back
      return;
    }
    const vis = isVisible(currentEl);
    if (vis !== lastVisible) {
      lastVisible = vis;
      onChange?.(vis, currentEl);
    }
  };

  const arm = () => {
    waitForElement(selector, { root, timeout: 0 }).then((el) => {
      currentEl = el;
      // initial state + log
      tick();

      // attribute changes on the element (class/style/hidden/aria-hidden)
      attrObs = new MutationObserver(tick);
      attrObs.observe(el, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
      });

      // DOM changes that might affect visibility or removal
      domObs = new MutationObserver(tick);
      domObs.observe(document.documentElement, { childList: true, subtree: true });

      // size changes (often paired with show/hide)
      ro = new ResizeObserver(tick);
      ro.observe(el);

      // safety net: periodic check (covers opacity transitions, etc.)
      poll = setInterval(tick, 500);
    });
  };

  arm();
  return () => teardown();
}

// --- usage for Snize ---
observeShowHide('#snize-instant-search-results', {
  onChange(visible, el) {
    if (visible) {
      console.log('Snize results are visible:', el);

      const body = document.body;
      body.classList.add('fixed');
    } else {
      console.log('Snize results are hidden');
      const body = document.body;
      body.classList.remove('fixed');
    }
  }
});

let lastSearchUrl = null;

async function updateCollectionWithSearch(url) {
  // remember the last ajax URL, including q, type, filters, etc.
  lastSearchUrl = url;

  const res = await fetch(url, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  const html = await res.text();

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;

  // 1) Replace .collection (grid + "View More" text + data-current-page)
  const newCollection = wrapper.querySelector('#ProductGridContainer .collection');
  const currentCollection = document.querySelector('#ProductGridContainer .collection');
  if (newCollection && currentCollection) {
    currentCollection.replaceWith(newCollection);
  }

  // 2) Replace active filters
  const newActiveFilters = wrapper.querySelector('.active--collection-filters');
  const currentActiveFilters = document.querySelector('.active--collection-filters');
  if (newActiveFilters && currentActiveFilters) {
    currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
  }

  // 3) Replace filters JSON
  const newFiltersJson = wrapper.querySelector('#activeFiltersJson');
  const currentFiltersJson = document.querySelector('#activeFiltersJson');
  if (newFiltersJson && currentFiltersJson) {
    currentFiltersJson.textContent = newFiltersJson.textContent;
  }

  // 4) Replace pagination row (numbered links)
  const newPaginationRow = wrapper.querySelector('.pagination-row[data-ajax-container]');
  const currentPaginationRow = document.querySelector('.pagination-row[data-ajax-container]');
  if (newPaginationRow && currentPaginationRow) {
    currentPaginationRow.innerHTML = newPaginationRow.innerHTML;
  }

  // re-bind anything if needed
  // window.initCollectionFilters && window.initCollectionFilters();
}

document.addEventListener('click', (e) => {
  const link = e.target.closest('.pagination-row[data-ajax-container] a');
  if (!link) return;

  e.preventDefault();

  const base = new URL(lastSearchUrl || link.href, window.location.origin);

  const linkUrl = new URL(link.href, window.location.origin);
  const pageFromHref = linkUrl.searchParams.get('page');
  const pageFromData = link.dataset.page;
  const page = pageFromHref || pageFromData || '1';

  base.searchParams.set('page', page);
  base.searchParams.set('view', 'ajax');

  const ajaxUrl = base.toString();

  const visible = new URL(window.location.href);
  visible.searchParams.set('page', page);
  window.history.replaceState({}, '', visible.toString());

  updateCollectionWithSearch(ajaxUrl);
});

document.addEventListener('click', (e) => {
  const btn = e.target.closest('.paginate-more');
  if (!btn) return;

  e.preventDefault();
  // stop theme’s collection-paginate JS from also handling this
  e.stopPropagation();
  if (e.stopImmediatePropagation) {
    e.stopImmediatePropagation();
  }

  const nextPage = btn.dataset.nextPage || '2';

  // Base: lastSearchUrl (has q=, type=product, etc). Fallback to current URL if somehow missing.
  const base = new URL(lastSearchUrl || window.location.href, window.location.origin);
  base.searchParams.set('page', nextPage);
  base.searchParams.set('view', 'ajax');

  const ajaxUrl = base.toString();

  // Update browser URL so ?page= is correct (but no view=ajax in the bar)
  const visible = new URL(window.location.href);
  visible.searchParams.set('page', nextPage);
  window.history.replaceState({}, '', visible.toString());

  updateCollectionWithSearch(ajaxUrl);
});

(function () {
  const GWP_THRESHOLD = 25000; // $250 in cents
  const GWP_WIDGET_ID = '246853';

  let gwpReloadScheduled = false;

  function getSmartCart() {
    return window.Rebuy && window.Rebuy.SmartCart;
  }

  function getCart() {
    return window.Rebuy && window.Rebuy.Cart;
  }

  function isGwpItem(item) {
    const props = item.properties || {};
    return (
      props._attribution === 'Rebuy Cart' &&
      props._source === 'Rebuy' &&
      String(props._widget_id) === GWP_WIDGET_ID
    );
  }

  async function removeLineItemByKey(key) {
    if (!key) return;

    console.log('[Rebuy GWP] Removing line via /cart/change.js', key);

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (!res.ok) {
        console.error('[Rebuy GWP] Failed to remove item', res.status, res.statusText);
      } else {
        const json = await res.json();
        console.log('[Rebuy GWP] Cart updated', json);

        // If we're on the main cart page, force a one-time reload
        if (window.location.pathname === '/cart' && !gwpReloadScheduled) {
          gwpReloadScheduled = true;
          console.log('[Rebuy GWP] Reloading /cart to sync UI');
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('[Rebuy GWP] Error removing item', err);
    }
  }

  function enforceGwpThreshold() {
    const SmartCart = getSmartCart();
    const Cart = getCart();

    if (!SmartCart || !SmartCart.items) return;

    if (!Cart || !Cart.cart || typeof Cart.cart.total_price !== 'number') {
      console.log('[Rebuy GWP] Cart not ready yet, skipping');
      return;
    }

    const subtotal = Cart.cart.total_price; // cents
    const items = SmartCart.items();

    console.log('[Rebuy GWP] subtotal (cents):', subtotal, 'threshold:', GWP_THRESHOLD);
    console.log('[Rebuy GWP] items:', items);

    if (subtotal >= GWP_THRESHOLD) return;

    const gwpItems = items.filter(isGwpItem);
    console.log('[Rebuy GWP] GWP items found:', gwpItems);

    gwpItems.forEach((item) => {
      console.log('[Rebuy GWP] Removing GWP item', item);
      removeLineItemByKey(item.key);
    });
  }

  document.addEventListener('rebuy:smartcart.init', function () {
    console.log('[Rebuy GWP] rebuy:smartcart.init fired');
    enforceGwpThreshold();
  });

  document.addEventListener('rebuy:cart.change', function () {
    console.log('[Rebuy GWP] rebuy:cart.change fired');
    enforceGwpThreshold();
  });

  // optional: polling fallback
  const interval = setInterval(function () {
    const SmartCart = getSmartCart();
    const Cart = getCart();
    if (SmartCart && Cart && Cart.cart && typeof Cart.cart.total_price === 'number') {
      console.log('[Rebuy GWP] SmartCart + Cart ready via polling');
      enforceGwpThreshold();
      clearInterval(interval);
    }
  }, 300);
})();

document.addEventListener('click', (event) => {
  const link = event.target.closest('.pagination__number .pagination__link, .pagination__prev, .pagination__next');
  if (!link) return;

  const productGridContainer = document.getElementById('ProductGridContainer');
  if (!productGridContainer) return;

  // Calculate total header offset from ALL sticky/fixed header groups
  const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
  let headerOffset = 0;

  headerGroups.forEach((el) => {
    const style = window.getComputedStyle(el);
    const isStickyOrFixed =
      style.position === 'sticky' || style.position === 'fixed';

    if (!isStickyOrFixed) return;

    const rect = el.getBoundingClientRect();

    // Only consider header groups that are on-screen near the top
    const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
    if (!isVisible) return;

    // We care about how far down from the top of the viewport the bottom sits
    const bottom = rect.bottom; // relative to viewport top (0)
    if (bottom > headerOffset) {
      headerOffset = bottom;
    }
  });

  // Now scroll the grid so it sits just below the headers
  const gridRect = productGridContainer.getBoundingClientRect();
  const targetY =
    window.pageYOffset + gridRect.top - headerOffset;

  window.scrollTo({
    top: targetY,
    behavior: 'smooth',
  });
});